import { useEffect, useState } from "react";

import { SOURCE } from "@/constants";
import { useEpisode } from "@/containers/Episode/context";
import { PlayIcon } from "@/Icons";
import { cx } from "@/utils";
import Clickable from "@components/Clickable";

import Fallback from "./Fallback";

import styles from "../Cover.module.css";

const getTikTokClientCache = (episodePublicUrl) => {
  return {
    get: () => {
      try {
        const data = sessionStorage.getItem(`tiktok-${episodePublicUrl}`);
        if (data) {
          return JSON.parse(data);
        }
      } catch {
        // Browser might not support sessionStorage or data might be corrupted.
      }
      return undefined;
    },
    set: (value) => {
      try {
        sessionStorage.setItem(
          `tiktok-${episodePublicUrl}`,
          JSON.stringify(value)
        );
      } catch {
        // Browser might not support sessionStorage
      }
    },
  };
};

const TiktokCover = ({ episode }) => {
  const { episode_public_url } = episode;
  const [oembedCover, setOembedCover] = useState(
    () => getTikTokClientCache(episode_public_url).get()?.thumbnail_url ?? ""
  );
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [imgLoadFailed, setImgLoadFailed] = useState(false);
  const { setMediaModalOpen } = useEpisode();

  // Side-effects: re-read the cached cover when the episode changes.
  const [prevUrl, setPrevUrl] = useState(episode_public_url);
  if (episode_public_url !== prevUrl) {
    setPrevUrl(episode_public_url);
    setOembedCover(
      getTikTokClientCache(episode_public_url).get()?.thumbnail_url ?? ""
    );
  }

  useEffect(() => {
    const cache = getTikTokClientCache(episode_public_url);
    const cachedValue = cache.get();
    if (cachedValue?.thumbnail_url) {
      return;
    }
    fetch(`https://www.tiktok.com/oembed?url=${episode_public_url}`, {
      method: "GET",
    })
      .then((r) => r.json())
      .then((data) => {
        setOembedCover(data.thumbnail_url);
        cache.set(data);
      })
      .catch(() => {
        //  silence errors from TikTok blocking us.
        //  Any errors triggered as CORS will not be caught here since the
        //  browser does not allow us to look at the response.
        //  These errors occur when we've been blocked.
        //  More info about TikTok request blocking: https://stackoverflow.com/questions/48728173/how-do-i-fix-cors-issue-in-fetch-api
      });
  }, [episode_public_url]);

  return imgLoadFailed || !oembedCover ? (
    <Fallback source={SOURCE.TIKTOK} />
  ) : (
    <>
      <img
        onClick={() => setMediaModalOpen(true)}
        loading="lazy"
        src={oembedCover}
        className={cx(styles.img, { [styles.visible]: coverLoaded })}
        onLoad={() => setCoverLoaded(true)}
        onError={() => setImgLoadFailed(true)}
        alt=""
      />
      {episode.episode_guid && (
        <Clickable
          data-testid="tiktok-embed-open"
          className={styles.play}
          onClick={() => setMediaModalOpen(true)}
        >
          <PlayIcon fill="currentColor" />
        </Clickable>
      )}
    </>
  );
};
export default TiktokCover;
